/**
 * Task steps list component for displaying execution steps
 */

import { ExternalLink, Clock, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import type { TaskStep } from '../../types/anchor-browser'

interface TaskStepsListProps {
  steps: TaskStep[]
  currentStep?: number
  className?: string
}

function formatUrl(url: string): string {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname + urlObj.pathname
  } catch {
    return url
  }
}

export function TaskStepsList({ steps, currentStep, className }: TaskStepsListProps) {
  if (steps.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Execution Steps
          </CardTitle>
          <CardDescription>
            Step-by-step progress of the task execution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No steps recorded yet</p>
            <p className="text-sm">Steps will appear as the task executes</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Execution Steps
          <Badge variant="secondary">{steps.length} steps</Badge>
        </CardTitle>
        <CardDescription>
          Step-by-step progress of the task execution
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          <div className="p-6 space-y-4">
            {steps.map((step, index) => {
              const isCurrentStep = currentStep !== undefined && step.step === currentStep
              const isCompleted = currentStep !== undefined && step.step < currentStep
              
              return (
                <div key={step.id} className="relative">
                  {/* Step connector line */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-4 top-8 w-0.5 h-16 bg-border" />
                  )}
                  
                  <div className="flex gap-4">
                    {/* Step number and status */}
                    <div className="flex-shrink-0">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                        ${isCurrentStep 
                          ? 'bg-primary text-primary-foreground' 
                          : isCompleted 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-muted text-muted-foreground'
                        }
                      `}>
                        {isCompleted ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          step.step
                        )}
                      </div>
                    </div>

                    {/* Step content */}
                    <div className="flex-1 min-w-0 pb-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className={`
                            text-sm font-medium leading-tight
                            ${isCurrentStep ? 'text-primary' : 'text-foreground'}
                          `}>
                            Step {step.step}
                            {isCurrentStep && (
                              <Badge variant="default" className="ml-2 text-xs">
                                Current
                              </Badge>
                            )}
                          </h4>
                        </div>
                        
                        {/* URL badge */}
                        {step.url && (
                          <a
                            href={step.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {formatUrl(step.url)}
                          </a>
                        )}
                      </div>

                      {/* Previous goal evaluation */}
                      {step.evaluation_previous_goal && (
                        <div className="mb-3">
                          <p className="text-xs text-muted-foreground mb-1">Previous Goal Evaluation:</p>
                          <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded text-wrap">
                            {step.evaluation_previous_goal}
                          </p>
                        </div>
                      )}

                      {/* Next goal */}
                      {step.next_goal && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">
                            {isCurrentStep ? 'Current Goal:' : 'Goal:'}
                          </p>
                          <p className={`
                            text-sm p-2 rounded text-wrap
                            ${isCurrentStep 
                              ? 'bg-primary/10 text-primary border border-primary/20' 
                              : 'bg-muted/50 text-foreground'
                            }
                          `}>
                            {step.next_goal}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Separator between steps */}
                  {index < steps.length - 1 && (
                    <Separator className="my-4" />
                  )}
                </div>
              )
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
